import { useMemo } from "react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, BarChart3, Zap, Users } from "lucide-react";
import type { ChartDataPoint } from "@/hooks/useAdminData";

interface Props {
  data: ChartDataPoint[];
  loading: boolean;
}

function formatTimestamp(ts: string) {
  const d = new Date(ts);
  const now = Date.now();
  const diff = now - d.getTime();
  if (diff < 25 * 60 * 60 * 1000) {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

const tooltipStyle = {
  fontSize: 12,
  borderRadius: 12,
  border: "1px solid hsl(var(--border))",
  background: "hsl(var(--card) / 0.95)",
  backdropFilter: "blur(12px)",
  boxShadow: "0 8px 32px -8px hsl(var(--primary) / 0.15)",
};

export function AdminCharts({ data, loading }: Props) {
  const formattedData = useMemo(
    () => data.map((d) => ({ ...d, label: formatTimestamp(d.timestamp) })),
    [data]
  );

  if (loading) {
    return (
      <Card className="glass animate-pulse-slow">
        <CardContent className="flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-primary/60" />
            <span className="text-xs text-muted-foreground">Loading charts…</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data.length) {
    return (
      <Card className="glass">
        <CardContent className="flex flex-col items-center justify-center py-20 text-muted-foreground text-sm gap-2">
          <BarChart3 className="w-10 h-10 text-muted-foreground/30" />
          <span>No data for this time range</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="animate-fade-in">
      <Tabs defaultValue="usage" className="space-y-4">
        <TabsList className="bg-card/80 backdrop-blur-xl border border-border/50">
          <TabsTrigger value="usage" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary gap-1.5">
            <Zap className="w-3.5 h-3.5" />
            Usage
          </TabsTrigger>
          <TabsTrigger value="billing" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary gap-1.5">
            <BarChart3 className="w-3.5 h-3.5" />
            Billing
          </TabsTrigger>
          <TabsTrigger value="growth" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary gap-1.5">
            <Users className="w-3.5 h-3.5" />
            Growth
          </TabsTrigger>
        </TabsList>

        <TabsContent value="usage">
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="glass card-hover group">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <div className="p-1 rounded-md bg-primary/10"><BarChart3 className="w-3.5 h-3.5 text-primary" /></div>
                  Runs Over Time
                </CardTitle>
              </CardHeader>
              <CardContent className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={formattedData}>
                    <defs>
                      <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border/30" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} className="fill-muted-foreground" axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10 }} className="fill-muted-foreground" axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "hsl(var(--primary) / 0.05)" }} />
                    <Bar dataKey="runs" fill="url(#barGrad)" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="glass card-hover group">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <div className="p-1 rounded-md bg-accent/10"><Zap className="w-3.5 h-3.5 text-accent" /></div>
                  Credits Consumed
                </CardTitle>
              </CardHeader>
              <CardContent className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={formattedData}>
                    <defs>
                      <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border/30" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} className="fill-muted-foreground" axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10 }} className="fill-muted-foreground" axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={tooltipStyle} cursor={{ stroke: "hsl(var(--primary) / 0.2)" }} />
                    <Area type="monotone" dataKey="credits_consumed" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#areaGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="billing">
          <Card className="glass card-hover">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <div className="p-1 rounded-md bg-primary/10"><BarChart3 className="w-3.5 h-3.5 text-primary" /></div>
                Top-up Credits Purchased
              </CardTitle>
            </CardHeader>
            <CardContent className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={formattedData}>
                  <defs>
                    <linearGradient id="topupGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--accent))" stopOpacity={0.9} />
                      <stop offset="100%" stopColor="hsl(var(--accent))" stopOpacity={0.4} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border/30" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} className="fill-muted-foreground" axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10 }} className="fill-muted-foreground" axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "hsl(var(--primary) / 0.05)" }} />
                  <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                  <Bar dataKey="topup_purchased" name="Top-up Purchased" fill="url(#topupGrad)" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="credits_consumed" name="Credits Consumed" fill="url(#barGrad)" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="growth">
          <Card className="glass card-hover">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <div className="p-1 rounded-md bg-reqqy-success/10"><Users className="w-3.5 h-3.5 text-reqqy-success" /></div>
                New Users
              </CardTitle>
            </CardHeader>
            <CardContent className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={formattedData}>
                  <defs>
                    <linearGradient id="growthGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--reqqy-success))" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="hsl(var(--reqqy-success))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border/30" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} className="fill-muted-foreground" axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10 }} className="fill-muted-foreground" allowDecimals={false} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={tooltipStyle} cursor={{ stroke: "hsl(var(--reqqy-success) / 0.2)" }} />
                  <Area type="monotone" dataKey="new_users" stroke="hsl(var(--reqqy-success))" strokeWidth={2} fill="url(#growthGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
